/* eslint-disable react-refresh/only-export-components */
/**
 * ColorPicker Component
 * Allows users to select from preset colors for tag visualization
 *
 * Features:
 * - 8 preset colors in grid layout
 * - Optional "No color" selection
 * - Visual selected state with checkmark
 * - Accessible with keyboard navigation
 */

import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'

export interface ColorOption {
  label: string
  value: string
  className: string
}

/**
 * Preset color options for tags
 * Using Tailwind color palette for consistency
 */
export const COLOR_OPTIONS: ColorOption[] = [
  { label: 'Red', value: '#EF4444', className: 'bg-red-500' },
  { label: 'Amber', value: '#F59E0B', className: 'bg-amber-500' },
  { label: 'Green', value: '#10B981', className: 'bg-green-500' },
  { label: 'Blue', value: '#3B82F6', className: 'bg-blue-500' },
  { label: 'Indigo', value: '#6366F1', className: 'bg-indigo-500' },
  { label: 'Violet', value: '#8B5CF6', className: 'bg-violet-500' },
  { label: 'Pink', value: '#EC4899', className: 'bg-pink-500' },
  { label: 'Gray', value: '#6B7280', className: 'bg-gray-500' },
]

export interface ColorPickerProps {
  /**
   * Currently selected color (hex string)
   * Undefined/empty for no color selected
   */
  value?: string

  /**
   * Callback when color is selected
   * @param color - Hex color string or undefined for no color
   */
  onChange: (color: string | undefined) => void

  /**
   * Optional label for the color picker
   */
  label?: string

  /**
   * Whether to show "No color" option
   * @default true
   */
  showNoColor?: boolean

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Whether the field is required
   * @default false
   */
  required?: boolean
}

export function ColorPicker({
  value,
  onChange,
  label = 'Tag Color',
  showNoColor = true,
  className,
  required = false,
}: ColorPickerProps) {
  const isNoColorSelected = !value || value === ''

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label */}
      <label className="block text-sm font-medium text-gray-900">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {/* Color Grid */}
      <div className="grid grid-cols-4 gap-3">
        {/* No Color Option */}
        {showNoColor && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className={cn(
              'h-10 w-10 rounded-full border-2 transition-all',
              'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#0033A0] focus:ring-offset-2',
              'relative bg-white',
              isNoColorSelected
                ? 'border-[#0033A0] ring-2 ring-[#0033A0] ring-offset-2'
                : 'border-gray-300'
            )}
            aria-label="No color"
            aria-pressed={isNoColorSelected}
          >
            {/* Diagonal line for "no color" */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="h-[2px] w-8 rotate-45 bg-gray-400"
                aria-hidden="true"
              />
            </div>

            {/* Checkmark when selected */}
            {isNoColorSelected && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Check
                  className="h-5 w-5 text-[#0033A0]"
                  strokeWidth={3}
                  aria-hidden="true"
                />
              </div>
            )}
          </button>
        )}

        {/* Color Options */}
        {COLOR_OPTIONS.map(color => {
          const isSelected = value === color.value

          return (
            <button
              key={color.value}
              type="button"
              onClick={() => onChange(color.value)}
              className={cn(
                'h-10 w-10 rounded-full border-2 transition-all',
                'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#0033A0] focus:ring-offset-2',
                color.className,
                isSelected
                  ? 'border-[#0033A0] ring-2 ring-[#0033A0] ring-offset-2'
                  : 'border-transparent'
              )}
              aria-label={`${color.label} color`}
              aria-pressed={isSelected}
            >
              {/* Checkmark when selected */}
              {isSelected && (
                <div className="flex h-full items-center justify-center">
                  <Check
                    className="h-5 w-5 text-white drop-shadow-md"
                    strokeWidth={3}
                    aria-hidden="true"
                  />
                </div>
              )}
            </button>
          )
        })}
      </div>

      {/* Helper text */}
      {!required && (
        <p className="text-xs text-gray-500">
          Optional: Choose a color to help identify this tag
        </p>
      )}
    </div>
  )
}
